import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {AudienceModel, AudienceRequestModel} from '../../../redux/campaignTypes'
import EmployerSelector from '../../EmployerSelector'
import {SearchResultItem} from '../../SearchAndSelectField'
import {Colors} from '../../../constants/colors'
import S2 from '../../typography/S2'

interface Props {
  audience: Extract<AudienceModel, { type: 'activeEmployees' }> | undefined
  onChange: (audience: Extract<AudienceRequestModel, { type: 'activeEmployees' }>) => void
}

const ActiveEmployeesAudienceEditor = ({audience, onChange}: Props) => {
  const styles = useStyles()
  const [allEmployers, setAllEmployers] = useState<boolean>(false)
  const [selectedEmployers, setSelectedEmployers] = useState<SearchResultItem[]>([])

  useEffect(() => {
    if (audience?.employers && audience.employers.length > 0) {
      const items = audience.employers.map(x => ({name: x.name, value: x.id}))
      setSelectedEmployers(items)
      setAllEmployers(false)
    } else {
      setSelectedEmployers([])
      setAllEmployers(true)
    }
  }, [audience])

  useEffect(() => {
    const next: AudienceRequestModel =
      allEmployers
        ? {type: 'activeEmployees'}
        : {type: 'activeEmployees', employerIds: selectedEmployers.map(e => e.value)}

    onChange(next)
  }, [allEmployers, selectedEmployers, onChange])

  const handleEmployerSelected = (item: SearchResultItem | null) => {
    if (!item) return
    if (selectedEmployers.some(x => x.value === item.value)) return
    setSelectedEmployers(prev => [...prev, item])
  }

  const handleRemove = (value: string) => {
    setSelectedEmployers(prev => prev.filter(e => e.value !== value))
  }

  const renderSelected = () => {
    if (allEmployers) {
      return <S2 className={styles.hint}>Targeting active employees across all employers.</S2>
    }

    if (selectedEmployers.length === 0) {
      return <S2 className={styles.hint}>No employers selected. Search to add employers or toggle "All employers".</S2>
    }

    return (
      <div className={styles.chipsContainer}>
        {selectedEmployers.map(e => (
          <div className={styles.chip} key={e.value}>
            <span className={styles.chipText}>{e.name}</span>
            <button type='button' className={styles.removeBtn} onClick={() => handleRemove(e.value)}>×</button>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <label className={styles.toggleRow}>
        <input
          type='checkbox'
          checked={allEmployers}
          onChange={(e) => setAllEmployers(e.target.checked)}
        />
        <span>All employers</span>
      </label>
      {!allEmployers && (
        <EmployerSelector onEmployerSelected={handleEmployerSelected} value='' clearOnSelect/>
      )}
      {renderSelected()}
    </div>
  )
}

export default ActiveEmployeesAudienceEditor

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 12,
    background: Colors.darkGray3,
  },
  chipText: {
    fontFamily: 'Aestetico-Regular',
    color: Colors.gray0,
  },
  removeBtn: {
    border: 'none',
    background: 'transparent',
    color: Colors.gray0,
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    '&:hover': {
      background: Colors.darkGray4
    }
  },
  hint: {
    color: Colors.gray0,
  }
})
